import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bookingAPI, paymentAPI } from '../services/api';
import {
  clearPendingStayBooking,
  releasePendingStayBooking,
  getPendingStayId
} from '../utils/pendingStayBooking';
import { transformStayBookingForConfirmation } from '../utils/stayBookingConfirmation';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Cashfree return_url — verify once, load booking once, then confirmation (no duplicate APIs there).
 */
const StayPaymentReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const handledRef = useRef(false);
  const [message, setMessage] = useState('Confirming your payment…');

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const orderId =
      searchParams.get('order_id') ||
      searchParams.get('orderId') ||
      sessionStorage.getItem('cashfree_order_id') ||
      sessionStorage.getItem('bfp_pending_stay_order');

    if (!orderId) {
      navigate('/', { replace: true });
      return;
    }

    (async () => {
      try {
        let result = null;
        const maxAttempts = 4;

        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          result = await paymentAPI.verifyPayment(orderId);
          if (result.paymentStatus === 'SUCCESS') break;
          if (result.paymentStatus === 'FAILED') break;
          if (attempt < maxAttempts - 1) {
            await sleep(1200);
          }
        }

        if (result?.paymentStatus === 'SUCCESS') {
          clearPendingStayBooking();
          sessionStorage.removeItem('cashfree_order_id');

          let bookingFromApi = null;
          const bookingId = result.bookingId || orderId.replace(/^order_/, '');
          if (bookingId) {
            try {
              const bookingResponse = await bookingAPI.getBookingById(bookingId);
              bookingFromApi = bookingResponse.booking || null;
            } catch (fetchErr) {
              console.warn('Could not load booking for confirmation:', fetchErr);
            }
          }

          const confirmationBooking = bookingFromApi
            ? transformStayBookingForConfirmation(bookingFromApi)
            : null;

          navigate(
            `/booking-confirmation?order_id=${encodeURIComponent(orderId)}`,
            {
              replace: true,
              state: {
                skipPaymentReturn: true,
                paymentVerified: true,
                orderId,
                bookingId,
                bookingFromApi,
                confirmationBooking
              }
            }
          );
          return;
        }

        if (result?.paymentStatus === 'FAILED') {
          const bookingId = result.bookingId || orderId.replace(/^order_/, '');
          await releasePendingStayBooking(bookingId);

          const stayId = result.stayId || getPendingStayId();
          clearPendingStayBooking();

          if (stayId) {
            navigate(`/property/${stayId}/booking-review`, {
              replace: true,
              state: {
                paymentIncomplete: true,
                paymentError: 'Payment failed. You can try again.'
              }
            });
          } else {
            navigate('/', { replace: true });
          }
          return;
        }

        const stayId = result?.stayId || getPendingStayId();
        setMessage('Payment was not completed. Taking you back to checkout…');

        if (stayId) {
          navigate(`/property/${stayId}/booking-review`, {
            replace: true,
            state: {
              paymentIncomplete: true,
              paymentError:
                'Payment was not completed. Please tap Pay now again to open the payment page.',
              orderId
            }
          });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        console.warn('Stay payment return error:', err?.response?.data || err.message);
        const bookingId = orderId.replace(/^order_/, '');
        try {
          const retry = await paymentAPI.verifyPayment(orderId);
          if (retry.paymentStatus === 'SUCCESS') {
            clearPendingStayBooking();
            let bookingFromApi = null;
            if (retry.bookingId) {
              try {
                const bookingResponse = await bookingAPI.getBookingById(retry.bookingId);
                bookingFromApi = bookingResponse.booking || null;
              } catch (_) {
                /* optional */
              }
            }
            navigate(
              `/booking-confirmation?order_id=${encodeURIComponent(orderId)}`,
              {
                replace: true,
                state: {
                  skipPaymentReturn: true,
                  paymentVerified: true,
                  orderId,
                  bookingId: retry.bookingId,
                  bookingFromApi,
                  confirmationBooking: bookingFromApi
                    ? transformStayBookingForConfirmation(bookingFromApi)
                    : null
                }
              }
            );
            return;
          }
          if (retry.paymentStatus === 'FAILED') {
            await releasePendingStayBooking(bookingId);
            clearPendingStayBooking();
            const stayId = retry.stayId || getPendingStayId();
            if (stayId) {
              navigate(`/property/${stayId}/booking-review`, {
                replace: true,
                state: { paymentIncomplete: true }
              });
              return;
            }
          }
        } catch (_) {
          /* do not release inventory on transient verify errors */
        }

        const stayId = getPendingStayId();
        if (stayId) {
          navigate(`/property/${stayId}/booking-review`, {
            replace: true,
            state: {
              paymentIncomplete: true,
              paymentError: 'Could not confirm payment. Please try Pay now again.'
            }
          });
        } else {
          navigate('/', { replace: true });
        }
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
      <div className="text-center px-6">
        <div className="w-10 h-10 border-4 border-[#FF5E4C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600 text-sm">{message}</p>
      </div>
    </div>
  );
};

export default StayPaymentReturn;
